/** Who is in the office, grouped by where they are standing. */

import { zoneById } from '@virtual-office/shared';

import type { Store } from '../state.js';
import { portraitSprite } from '../sprite.js';
import { $, clear, el } from './dom.js';

export class RosterPanel {
  private readonly list = $<HTMLUListElement>('roster-list');
  private readonly population = $('population');
  private readonly statusInput = $<HTMLInputElement>('status-input');

  constructor(
    private readonly store: Store,
    private readonly onStatusChange: (status: string) => void,
  ) {
    let debounce = 0;
    this.statusInput.addEventListener('input', () => {
      window.clearTimeout(debounce);
      debounce = window.setTimeout(() => this.onStatusChange(this.statusInput.value), 400);
    });
    this.statusInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === 'Escape') this.statusInput.blur();
      event.stopPropagation();
    });

    this.store.on('players', () => this.render());
    this.store.on('welcome', () => {
      this.statusInput.value = this.store.me?.status ?? '';
      this.render();
    });
    this.render();
  }

  private render(): void {
    clear(this.list);
    const players = [...this.store.players.values()].sort((a, b) => a.name.localeCompare(b.name));
    this.population.textContent = `${players.length} here`;

    for (const player of players) {
      const zone = this.store.map ? zoneById(this.store.map, player.zoneId) : null;
      // An uploaded photo if there is one, otherwise their character's face —
      // never a plain coloured dot, which told you nothing.
      const face = el('span', { className: 'face' });
      if (player.avatarVersion > 0) {
        const img = el('img');
        img.src = `/avatars/${player.id}.png?v=${player.avatarVersion}`;
        img.alt = '';
        face.append(img);
      } else {
        face.append(portraitSprite(player.appearance, 3));
      }

      const name = el('span', { className: 'roster-name', text: player.name });
      if (player.id === this.store.meId) name.classList.add('is-me');

      const where = el('span', {
        className: 'roster-where',
        text: zone?.name ?? 'walking about',
      });

      const item = el('li', { className: 'roster-item' }, [face, name, where]);
      if (player.status) {
        item.append(el('span', { className: 'roster-status', text: player.status }));
      }
      // Departments and teams only. Personal tags never reach the client for
      // anyone but their owner.
      if (player.groups.length > 0) {
        const chips = el('span', { className: 'roster-groups' });
        for (const group of player.groups.slice(0, 4)) {
          chips.append(el('span', { className: `group-chip kind-${group.kind}`, text: group.name }));
        }
        item.append(chips);
      }
      this.list.append(item);
    }
  }
}
